import { Schema } from "effect";
import { OffsetRegion } from "./collab.ts";
import { ConnectionId, DocumentId } from "./ids.ts";

// The live-awareness wire value types (ADR-0013 amended by ADR-0043/0044/deferred-U14). Presence is
// **orthogonal** to the document text and review state: it carries **no**
// `version`/`seq` and never advances either cursor. A `headingLine` is a *source line number*
// (best-effort, self-healing on debounce — see the ADR), deliberately not version-stamped.
//
// ADR-0013 amended (ADR-0044): the agent peer mints a stable synthetic ConnectionId per session
// and registers a synthetic presence entry (tagged as "agent") via joinPresence. The presence
// hub interface is unchanged — the agent just uses the same joinPresence/leavePresence as any
// WS connection, keyed by its synthetic ConnectionId.
//
// U14 (co-viewer selections, deferred from the original plan): `selection` rides the SAME
// orthogonal presence channel as `position` — no new `version`/`seq`, no new wire surface. It is
// always reported in **HEAD coordinates** (the `OffsetRegion` shape already used for conflict
// regions/diagnostic anchors — reused rather than a bespoke schema, same `{ from, to }` shape).
// A viewer with an open draft maps their local (draft-buffer) selection through the draft's
// INVERSE ChangeSet before sending, so every viewer's selection on the wire addresses the SAME
// coordinate space regardless of whether the sender is drafting — see `presence-reporter.ts`'s
// outbound mapping and `selections-client.ts`'s inbound mapping for the full mechanics. Like
// `position`, deliberately not version-stamped: a selection reported against one viewer's
// momentarily-stale head self-heals on the next debounced report, and the receiving side keeps
// every tracked selection current by mapping it through every `commit-landed` it observes
// between reports (the diagnostics client's own "between frames, map through incoming lands"
// discipline, ADR-0045).

// Where a viewer is in the document: the topmost visible heading's source line. Bounded
// **non-negative `Schema.Int`** so a malformed/oversized line is rejected at the decode boundary and
// can never reach the presence hub.
export const Position = Schema.Struct({
  headingLine: Schema.Int.pipe(Schema.nonNegative()),
});
export type Position = typeof Position.Type;

// One viewer's presence as broadcast to co-viewers. Carries the *derived* display identity
// (`displayName`/`color`) keyed by the opaque per-connection `connectionId` — and **never** the
// `ViewerId` (the auth subject stays server-side, security F6). `position` is absent until the client
// reports one (a viewer present but not yet placed against a section). `selection` (U14) is absent
// until the client reports one AND whenever the viewer's selection is collapsed to a bare cursor —
// both read as "nothing to render" by the CM decoration layer, always in HEAD coordinates.
export const ViewerPresence = Schema.Struct({
  connectionId: ConnectionId,
  displayName: Schema.String,
  color: Schema.String,
  position: Schema.optional(Position),
  selection: Schema.optional(OffsetRegion),
});
export type ViewerPresence = typeof ViewerPresence.Type;

// The full presence set for one document (snapshot semantics, so a late subscriber needs no prior
// deltas). The hub publishes this on every change to a doc's set.
export const PresenceBroadcast = Schema.Struct({
  documentId: DocumentId,
  viewers: Schema.Array(ViewerPresence),
});
export type PresenceBroadcast = typeof PresenceBroadcast.Type;

// ─── Synthetic agent presence identity (ADR-0044 / U7) ───────────────────────
//
// The agent headless peer (U7) registers a synthetic presence entry so reviewers
// can see the agent in the presence list while a session is active. The agent is
// primarily visible as its named open draft/diff; the presence dot confirms the
// session is live. Exactly like a human ViewerPresence but tagged as "agent" so
// rendering can distinguish (e.g. an agent icon vs a human avatar).
//
// The hub interface (presence-hub.ts) requires no change — the agent mints a
// stable synthetic ConnectionId per session and calls joinPresence/leavePresence.

// A synthetic presence entry for the agent peer, tagged as "agent" for rendering.
export const AgentPresence = Schema.Struct({
  // The stable synthetic ConnectionId minted per agent session.
  connectionId: ConnectionId,
  // A stable display name for this agent session (e.g. "Claude Agent").
  displayName: Schema.String,
  // A stable color for this agent session (same derivation as viewer colors).
  color: Schema.String,
  // Agent position is optional and typically absent (no viewport to report).
  position: Schema.optional(Position),
  // Tag to distinguish agent from human viewers in the presence list.
  kind: Schema.Literal("agent"),
});
export type AgentPresence = typeof AgentPresence.Type;

// The human viewer presence kind (adding explicit kind field for parity).
// The existing ViewerPresence (without kind) remains the wire shape for human viewers
// for backward compat; AgentPresence carries the "agent" kind tag.
// U7 renders AgentPresence entries with a distinct affordance (e.g. robot icon).

// The combined presence entry union for consumers that need to handle both kinds.
// Callers that only need human presence use ViewerPresence directly (no change needed).
export const AnyPresenceEntry = Schema.Union(ViewerPresence, AgentPresence);
export type AnyPresenceEntry = typeof AnyPresenceEntry.Type;
